import { App } from 'antd';
import type { QueryKey, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosCustomError } from '../../services/http';

interface IUseMutationBaseOptions<TData, TError, TVariables, TContext>
  extends UseMutationOptions<TData, TError, TVariables, TContext> {
  invalidateCallback?: (data: TData, variables: TVariables) => QueryKey[];
  withErrorMessage?: boolean;
}

export const useMutationBase = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  options: IUseMutationBaseOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const { invalidateCallback, withErrorMessage = true, onSuccess, onError, ...rest } = options;
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables, TContext>({
    onSuccess: async (data, variables, context) => {
      const queryKeys = invalidateCallback?.(data, variables);

      if (queryKeys) {
        await Promise.all(
          queryKeys.map((key) => queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })),
        );
      }

      if (onSuccess) onSuccess(data, variables, context);
    },

    onError: (error, variables, context) => {
      if (withErrorMessage) {
        const { message: errorMessage, key } = (error as AxiosCustomError)?.response?.data || {};
        message.error(errorMessage || key);
      }

      if (onError) onError(error, variables, context);
    },

    ...rest,
  });
};
